// CommercialAddForm.jsx
import React, { useState, useContext, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { URL, getToken } from "../../utils";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select";
import { UserContext } from "../../contexts/UserContext";
import NumberInput from "../../components/NumberInput";
import CountryField from "../../components/CountryField";
import { useCountries } from "../../hooks/useCountries";

export default function AddCommercialForm() {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    objectif_mensuel: 0,
    objectif_journalier: "",
    telephone: "",
    statut: "Actif",
    sexe: "M",
    magasin: 0,
    situation_mat: "celibataire",
    date_naissance: "",
    adresse: "",
    pays: "",
  });
  const [isLoadingMagasins, setIsLoadingMagasins] = useState(false);
  const [errors, setErrors] = useState(null);
  const { countries } = useCountries();

  const navigate = useNavigate();
  const token = getToken();
  const [magasins, setMagasins] = useState([
    {
      value: 0,
      label: "",
    },
  ]);

  const { user } = useContext(UserContext);

  const fectData = useCallback(async () => {
    try {
      setIsLoadingMagasins(true);
      const response = await axios.get(`${URL}Magasins/magasins-list`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const options = response.data.map((magasin) => ({
          value: parseInt(magasin.id),
          label: magasin.nom,
        }));
        setMagasins(options);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des magasins");
    } finally {
      setIsLoadingMagasins(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user.is_caissier) {
      fectData();
    }
  }, [user.is_caissier, fectData]);

  const handleChange = (e) => {
    // Si e est un événement (pour les inputs standards)
    if (e && e.target) {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Fonction spécifique pour les NumberInputs
  const handleNumberChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.nom || !formData.prenom || !formData.objectif_mensuel) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    formData.objectif_mensuel = parseInt(formData.objectif_mensuel);
    formData.objectif_journalier = parseInt(formData.objectif_journalier);

    if (user.is_caissier) {
      formData.magasin = user.magasin_id;
    }

    axios
      .post(`${URL}Magasins/commercial/create-commercial`, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.status === 201) {
          toast.success("Commercial créé avec succès !");
          navigate("/commercials");
        }
      })
      .catch((error) => {
        if (error.status === 400 && error.response?.data) {
          setErrors(error.response.data);
          toast.error("Veuillez corriger les erreurs dans le formulaire");
        } else {
          setErrors(null);
          toast.error(
            error.response?.data?.error || "Erreur lors de la création"
          );
        }
      });
  };

  const handleMagasinChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      magasin: parseInt(selectedOption.value),
    }));
  };

  // Style personnalisé pour react-select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "42px",
      borderRadius: "0.5rem",
      borderColor: "#d1d5db",
      "&:hover": {
        borderColor: "#d1d5db",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#b45309" : "white",
      color: state.isSelected ? "white" : "#1f2937",
      "&:hover": {
        backgroundColor: "#fcd34d",
        color: "#1f2937",
      },
    }),
  };

  return (
    <div className="p-4 w-[min(600px,90%)] mx-auto mt-4 bg-white shadow-md rounded-md">
      <ToastContainer />
      <div className="flex gap-4 p-4 pb-0">
        <div className="p-3 bg-amber-200 rounded-full text-amber-600 grid justify-center items-center">
          <svg
            className="w-8 h-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        </div>
        <div className="">
          <h1 className="text-lg font-semibold">Ajouter un commercial</h1>
          <p className="text-sm text-gray-500">
            Créez un nouveau compte commercial
          </p>
        </div>
      </div>

      {errors && (
        <div
          className="p-4 mb-4 text-sm text-red-800 bg-red-100 rounded-lg"
          role="alert"
        >
          <span className="font-medium">Erreur :</span> Veuillez corriger les
          erreurs suivantes :
          <ul className="list-disc pl-5 mt-2">
            {Object.entries(errors).map(([field, message]) => (
              <li key={field}>
                {field.charAt(0).toUpperCase() + field.slice(1)}: {message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form className="p-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-900">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nom"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              value={formData.nom}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-900">
              Prénom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="prenom"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              value={formData.prenom}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* pays */}
        <div className="mb-4">
          <CountryField
            label="Pays"
            value={formData.pays}
            onChange={(selectedOption) =>
              setFormData((prev) => ({
                ...prev,
                pays: selectedOption ? selectedOption.value : "",
              }))
            }
            countries={countries}
            required
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Adresse <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="adresse"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.adresse}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Téléphone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="telephone"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.telephone}
            onChange={handleChange}
            required
          />
        </div>
        {!user.is_caissier ? (
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-900">
              Magasin <span className="text-red-500">*</span>
            </label>
            <Select
              options={magasins}
              isLoading={isLoadingMagasins}
              onChange={handleMagasinChange}
              placeholder="Rechercher un magasin..."
              noOptionsMessage={() => "Aucun résultat"}
              styles={customStyles}
              isSearchable
            />
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-900">
              Objectif mensuel <span className="text-red-500">*</span>
            </label>
            <NumberInput
              name="objectif_mensuel"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              value={formData.objectif_mensuel}
              onChange={(value) =>
                handleNumberChange("objectif_mensuel", value)
              }
              required
            />
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-900">
              Objectif journalier <span className="text-red-500">*</span>
            </label>
            <NumberInput
              name="objectif_journalier"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              value={formData.objectif_journalier}
              onChange={(value) =>
                handleNumberChange("objectif_journalier", value)
              }
              required
            />
          </div>
        </div>
        {/* Sexe */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Genre
          </label>
          <select
            name="sexe"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.sexe}
            onChange={handleChange}
          >
            <option value="M">Masculin</option>
            <option value="F">Feminin</option>
          </select>
        </div>
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Date de naissance <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="date_naissance"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.date_naissance}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Situation matrimonial <span className="text-red-500">*</span>
          </label>
          <select
            name="situation_mat"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.situation_mat}
            onChange={handleChange}
          >
            <option value="marie">Marie</option>
            <option value="celibataire">Celibataire</option>
          </select>
        </div>
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Statut <span className="text-red-500">*</span>
          </label>
          <select
            name="statut"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.statut}
            onChange={handleChange}
          >
            <option value="Actif">Actif</option>
            <option value="Inactif">Inactif</option>
          </select>
        </div>

        <div className="grid grid-cols-2 items-center gap-4 mt-6">
          <Link
            to="/commercials"
            className="hover:text-white border border-gray-800 bg-gray-200 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
          >
            Annuler
          </Link>
          <button
            type="submit"
            className="text-white bg-amber-700 hover:bg-amber-800 focus:ring-4 focus:outline-none focus:ring-amber-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
          >
            Créer
          </button>
        </div>
      </form>
    </div>
  );
}
